/**
 * Input system: browser events -> semantic GameAction state.
 *
 * Level-triggered controls (flippers/plunger) are polled by App; edge-triggered
 * controls (nudges/view toggle/etc.) notify subscribers immediately.
 */
import { CONTROL_ALIASES, type GameAction } from './types';

type EdgeHandler = (action: GameAction) => void;
type GamepadLike = Pick<Gamepad, 'axes' | 'buttons' | 'connected' | 'mapping'>;
type GamepadIdentity = Pick<Gamepad, 'connected' | 'mapping'>;
type FocusDocument = { hasFocus?: () => boolean };
type GamepadNavigator = { getGamepads?: () => Iterable<GamepadLike | null | undefined> };

const GAMEPAD_AXIS_THRESHOLD = 0.55;

function buttonDown(gamepad: GamepadLike, index: number): boolean {
  const button = gamepad.buttons[index];
  return Boolean(button && (button.pressed || button.value >= 0.5));
}

export function isSupportedGamepad(gamepad: GamepadIdentity | null | undefined): boolean {
  return Boolean(gamepad?.connected && gamepad.mapping === 'standard');
}

export function hasSupportedGamepad(
  gamepads: Iterable<GamepadIdentity | null | undefined>,
): boolean {
  for (const gamepad of gamepads) {
    if (isSupportedGamepad(gamepad)) return true;
  }
  return false;
}

export function shouldPollGamepads(
  listening: boolean,
  hidden: boolean,
  focused: boolean,
  getGamepads: unknown,
): boolean {
  return listening && !hidden && focused && typeof getGamepads === 'function';
}

export function currentDocumentFocus(documentLike: FocusDocument): boolean {
  if (typeof documentLike.hasFocus !== 'function') return true;
  try {
    return documentLike.hasFocus();
  } catch {
    // Focus detection is only a guard against background controller work. If a
    // non-standard DOM implementation rejects the query, preserve the legacy
    // behavior and let blur/focus events maintain the state from here onward.
    return true;
  }
}

export function notifyInputEdgeHandlers(
  handlers: Iterable<EdgeHandler>,
  action: GameAction,
): void {
  for (const handler of handlers) {
    try {
      handler(action);
    } catch {
      // Input observers are optional consumers. One faulty UI/analytics handler
      // must not prevent the remaining observers from seeing the edge or abort
      // the gamepad polling loop that schedules the next frame afterward.
    }
  }
}

/** Standard-layout controller bindings shared by Xbox/PlayStation-style pads. */
export function actionsForGamepad(gamepad: GamepadLike): Set<GameAction> {
  const actions = new Set<GameAction>();
  if (!isSupportedGamepad(gamepad)) return actions;

  const horizontal = gamepad.axes[0] ?? 0;
  const vertical = gamepad.axes[1] ?? 0;

  if (buttonDown(gamepad, 4) || buttonDown(gamepad, 6)) actions.add('flipper-left');
  if (buttonDown(gamepad, 5) || buttonDown(gamepad, 7)) actions.add('flipper-right');

  // A / Cross both launches from menus and acts as the plunger in play. The
  // app ignores whichever semantic action is not relevant to the current phase.
  if (buttonDown(gamepad, 0)) {
    actions.add('start');
    actions.add('plunger');
  }

  if (buttonDown(gamepad, 14) || horizontal <= -GAMEPAD_AXIS_THRESHOLD) actions.add('nudge-left');
  if (buttonDown(gamepad, 15) || horizontal >= GAMEPAD_AXIS_THRESHOLD) actions.add('nudge-right');
  if (buttonDown(gamepad, 12) || vertical <= -GAMEPAD_AXIS_THRESHOLD) actions.add('nudge-up');

  if (buttonDown(gamepad, 9)) actions.add('pause');
  if (buttonDown(gamepad, 2)) actions.add('toggle-view');
  if (buttonDown(gamepad, 3)) actions.add('mute');

  return actions;
}

/**
 * Snapshot all actions for currently supported pads. A null result means there
 * is no supported controller connected, so the animation-frame polling loop can
 * stop completely until the browser emits a gamepad connection event.
 */
export function gamepadActionSnapshot(
  gamepads: Iterable<GamepadLike | null | undefined>,
): Set<GameAction> | null {
  const actions = new Set<GameAction>();
  let supported = false;
  for (const gamepad of gamepads) {
    if (!gamepad || !isSupportedGamepad(gamepad)) continue;
    supported = true;
    for (const action of actionsForGamepad(gamepad)) actions.add(action);
  }
  return supported ? actions : null;
}

/**
 * Controller discovery is optional. Privacy modes and embedded browser shells
 * can expose navigator.getGamepads while still throwing when it is called. Treat
 * that the same as having no supported controller so keyboard/pointer input can
 * continue to start and the browser can retry on a later gamepad event/focus.
 */
export function readGamepadActionSnapshot(
  navigatorLike: GamepadNavigator,
): Set<GameAction> | null {
  if (typeof navigatorLike.getGamepads !== 'function') return null;
  try {
    return gamepadActionSnapshot(navigatorLike.getGamepads());
  } catch {
    return null;
  }
}

export function actionForCode(code: string, keymap: Record<GameAction, string>): GameAction | null {
  for (const [action, key] of Object.entries(keymap)) {
    if (key === code) return action as GameAction;
  }
  for (const [action, aliases] of Object.entries(CONTROL_ALIASES)) {
    if (aliases?.includes(code)) return action as GameAction;
  }
  return null;
}

/**
 * Keep keyboard and pointer presses independent so releasing/cancelling one
 * physical input source cannot accidentally release the same semantic action
 * while another source is still held.
 */
export class LocalInputState {
  private keyboardPressed = new Map<GameAction, Set<string>>();
  private pointerPressed = new Set<GameAction>();

  pressKeyboard(action: GameAction, code: string = action): void {
    let codes = this.keyboardPressed.get(action);
    if (!codes) {
      codes = new Set<string>();
      this.keyboardPressed.set(action, codes);
    }
    codes.add(code);
  }

  releaseKeyboard(action: GameAction, code: string = action): void {
    const codes = this.keyboardPressed.get(action);
    if (!codes) return;
    codes.delete(code);
    if (codes.size === 0) this.keyboardPressed.delete(action);
  }

  releaseKeyboardCode(code: string): void {
    for (const [action, codes] of this.keyboardPressed) {
      if (!codes.delete(code)) continue;
      if (codes.size === 0) this.keyboardPressed.delete(action);
    }
  }

  pressPointer(action: GameAction): void {
    this.pointerPressed.add(action);
  }

  releasePointer(action: GameAction): void {
    this.pointerPressed.delete(action);
  }

  cancelPointers(): void {
    this.pointerPressed.clear();
  }

  clear(): void {
    this.keyboardPressed.clear();
    this.pointerPressed.clear();
  }

  isPressed(action: GameAction): boolean {
    return this.keyboardPressed.has(action) || this.pointerPressed.has(action);
  }
}

export class InputSystem {
  private localPressed = new LocalInputState();
  private gamepadPressed = new Set<GameAction>();
  private keymap: Record<GameAction, string>;
  private edgeHandlers = new Set<EdgeHandler>();
  private listening = false;
  private windowFocused = true;
  private gamepadFrame: number | null = null;

  constructor(keymap: Record<GameAction, string>) {
    this.keymap = keymap;
  }

  setKeymap(keymap: Record<GameAction, string>): void {
    this.keymap = keymap;
  }

  private actionFor(code: string): GameAction | null {
    return actionForCode(code, this.keymap);
  }

  private clearPressed = (): void => {
    this.localPressed.clear();
    this.gamepadPressed.clear();
  };

  private publishEdge(action: GameAction): void {
    notifyInputEdgeHandlers(this.edgeHandlers, action);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    const action = this.actionFor(event.code);
    if (!action) return;
    event.preventDefault();
    if (event.repeat) return;
    this.localPressed.pressKeyboard(action, event.code);
    this.publishEdge(action);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    // Release by the physical key that went up rather than by the action the
    // current keymap assigns to it. A binding can change while a key is held;
    // tying release to the new map would otherwise leave the old action latched.
    this.localPressed.releaseKeyboardCode(event.code);
    const action = this.actionFor(event.code);
    if (!action) return;
    event.preventDefault();
  };

  private onBlur = (): void => {
    this.windowFocused = false;
    this.clearPressed();
    this.stopGamepadPolling();
  };

  private onFocus = (): void => {
    this.windowFocused = true;
    this.refreshGamepadPolling();
  };

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      this.clearPressed();
      this.stopGamepadPolling();
      return;
    }
    // Some browsers restore document visibility before dispatching the matching
    // window focus event. Recheck the actual document focus state here so an
    // unfocused visible page does not restart controller RAF polling or seed
    // held buttons until it truly regains focus.
    this.windowFocused = currentDocumentFocus(document);
    this.refreshGamepadPolling();
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, input, textarea, select, a')) return;
    const action =
      event.button === 0 ? 'flipper-left' : event.button === 2 ? 'flipper-right' : null;
    if (!action) return;
    event.preventDefault();
    this.localPressed.pressPointer(action);
    this.publishEdge(action);
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return;
    if (event.button === 0) this.localPressed.releasePointer('flipper-left');
    if (event.button === 2) this.localPressed.releasePointer('flipper-right');
  };

  private onPointerCancel = (): void => {
    // Browsers can cancel a pointer without a corresponding pointerup (window
    // switch, touch gesture takeover, pointer capture loss). Clear pointer-held
    // controls without disturbing keys that are still physically down.
    this.localPressed.cancelPointers();
  };

  private onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private stopGamepadPolling(): void {
    if (this.gamepadFrame !== null) {
      window.cancelAnimationFrame(this.gamepadFrame);
      this.gamepadFrame = null;
    }
    this.gamepadPressed.clear();
  }

  private refreshGamepadPolling = (): void => {
    if (!shouldPollGamepads(this.listening, document.hidden, this.windowFocused, navigator.getGamepads)) {
      this.stopGamepadPolling();
      return;
    }

    const snapshot = readGamepadActionSnapshot(navigator);
    if (snapshot === null) {
      this.stopGamepadPolling();
      return;
    }

    if (this.gamepadFrame === null) {
      // Resuming polling is not a new physical press. Seed the current held
      // state first so refocusing the tab cannot synthesize pause/start/view
      // edges from buttons that were already down while input was inactive.
      this.gamepadPressed = snapshot;
      this.pollGamepads();
    }
  };

  private onGamepadConnected = (): void => {
    this.refreshGamepadPolling();
  };

  private onGamepadDisconnected = (): void => {
    this.refreshGamepadPolling();
  };

  private pollGamepads = (): void => {
    if (!shouldPollGamepads(this.listening, document.hidden, this.windowFocused, navigator.getGamepads)) {
      this.gamepadFrame = null;
      this.gamepadPressed.clear();
      return;
    }

    const next = readGamepadActionSnapshot(navigator);
    if (next === null) {
      this.gamepadFrame = null;
      this.gamepadPressed.clear();
      return;
    }

    for (const action of next) {
      if (!this.gamepadPressed.has(action)) this.publishEdge(action);
    }
    this.gamepadPressed = next;
    this.gamepadFrame = window.requestAnimationFrame(this.pollGamepads);
  };

  isPressed(action: GameAction): boolean {
    return this.localPressed.isPressed(action) || this.gamepadPressed.has(action);
  }

  onAction(handler: EdgeHandler): () => void {
    this.edgeHandlers.add(handler);
    return () => this.edgeHandlers.delete(handler);
  }

  start(): void {
    if (this.listening) return;
    this.windowFocused = currentDocumentFocus(document);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('gamepadconnected', this.onGamepadConnected);
    window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.listening = true;
    this.refreshGamepadPolling();
  }

  stop(): void {
    this.stopGamepadPolling();
    if (!this.listening) {
      this.clearPressed();
      return;
    }
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('gamepadconnected', this.onGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearPressed();
    this.listening = false;
  }
}
